/**
 * Connect-on-resume: the eager set, and the background sweep behind it.
 *
 * A resume restores the workspace immediately and opens only the connections
 * the workspace actually lands on — one for an ordinary tab, both endpoints for
 * a Schema/Data Compare tab, the linked connection for a Database Blueprint
 * tab — BEFORE handing the workspace back. Every other connection the session
 * declares is seeded into `activeConnections` as `disconnected` (the same shape
 * the Entra skip has always produced) so it still shows up in the Object
 * Explorer, still lights the tab-strip dot, and still raises the workspace
 * connection banner (`Docs/ConnectionBanner.md` — the designed producer pattern
 * for "this tab's connection isn't open").
 *
 * The rest are then opened by `sweepPendingLazyConnects`, which the restore
 * fires AFTER it commits — off the critical path, bounded-concurrency, and
 * never awaited. A session that has a connection gets that connection back;
 * an unreachable or VPN-gated server costs nothing but a background failure,
 * because the workspace is already on screen by the time it is dialed.
 *
 * Two populations deliberately stay out of the sweep and keep the original
 * on-first-use behaviour:
 *
 * - **password-prompt connections** — `connectPasswordPromptStore` is a
 *   singleton whose `request` resolves the previous prompt as *cancelled*, so
 *   two concurrent password connects silently fail all but the last. Their
 *   prompt belongs to a deliberate gesture (the tab click), not to a resume.
 * - **Entra-interactive connections** — they need an external, focus-stealing
 *   auth window; the restore drops them before they ever reach this module.
 *
 * The bookkeeping this module owns:
 *
 * - `markConnectionsLazy` records what the restore deliberately did NOT connect
 *   eagerly. Nothing outside that set is ever auto-connected — a connection the
 *   user disconnected by hand must stay disconnected, whether the sweep or a
 *   tab click comes looking for it.
 * - `requestLazyConnect` attempts each such connection AT MOST ONCE: the sweep
 *   and a concurrent tab activation join the same in-flight promise, and a
 *   failed attempt is not retried (the banner's Connect button is the retry
 *   path, so an unreachable server can't turn tab-switching into a connect
 *   storm).
 */
import { useConnectionStore } from '../../connections/store/connectionStore';
import { connectionNeedsPasswordPrompt } from '../../connections/utils/connectPasswordPredicate';
import { useSessionStore } from '../store/sessionStore';
import { tabConnectionIds } from '../utils/tabConnectionIds';
import type { Connection } from '../../../types/electron';
import type { Session } from '../types/sessionTypes';

/** Connections the restore deferred and that may still be connected on demand. */
const pendingLazyIds = new Set<string>();
/** In-flight lazy connects, keyed by connection id — the concurrency dedupe. */
const inFlightLazyConnects = new Map<string, Promise<boolean>>();

/**
 * Subscribers to changes in the deferred set.
 *
 * `pendingLazyIds` is the AUTHORITY for "this connection is still waiting to be
 * opened" — it is filled by the restore, consumed the instant a connect is
 * dialed (by the sweep or by a tab activation), forgotten when the user
 * disconnects, and dropped on session teardown. That is exactly the fact the Object Explorer needs to tell
 * a deferred connection apart from a broken one, but plain module state gives
 * React nothing to re-render on, and the tree only rebuilds when the CONNECTION
 * IDS change — never on a status flip. So the set gets a change signal rather
 * than a second copy of the truth living in the connection/tree state:
 *
 *   useSyncExternalStore(subscribeLazyConnectPending, () => pendingKind(id))
 */
const pendingLazyListeners = new Set<() => void>();

/** Observe the deferred set. Returns the unsubscribe. */
export function subscribeLazyConnectPending(listener: () => void): () => void {
  pendingLazyListeners.add(listener);
  return () => {
    pendingLazyListeners.delete(listener);
  };
}

/** Snapshot the listeners first — a listener may unsubscribe while notifying. */
function notifyLazyConnectPendingChanged(): void {
  Array.from(pendingLazyListeners).forEach((listener) => listener());
}

/**
 * Entra connections need an EXTERNAL, focus-stealing auth window, so nothing
 * may open them without a deliberate gesture aimed at that connection — not a
 * resume, not a tab click.
 *
 * A connection that merely lacks a stored password is NOT in this class: its
 * prompt is an in-app modal raised by the tab the user just clicked, and the
 * click is the intent. Those stay in lazy connect.
 */
export function isEntraInteractiveConnection(connection: Connection): boolean {
  const engine = connection.engine || 'mssql';
  if (engine === 'mssql') {
    return (
      connection.authentication === 'Microsoft Entra ID (Device Code)' ||
      connection.authentication === 'Microsoft Entra ID (Interactive Browser)'
    );
  }
  // Azure Storage (spec §6.1): an Interactive Browser sign-in steals focus the
  // same way, so a restored storage tab never auto-connects it. Azure CLI and
  // Service Principal are non-interactive and stay in lazy connect.
  if (engine === 'azurestorage') {
    return connection.authentication === 'Microsoft Entra ID (Interactive Browser)';
  }
  return false;
}

/**
 * The connections a resume opens eagerly: everything the restored workspace
 * lands on needs.
 *
 * Priority is "what will the user be looking at":
 *   1. ALL of the session's active tab's connections — one for an ordinary
 *      tab, both endpoints for a Schema/Data Compare tab (source first), the
 *      linked connection for a Database Blueprint tab. A compare tab is
 *      unusable with one side open, and the connection banner opens the other
 *      side the instant the restored tab renders, so declaring only one made
 *      the app promise less than it does;
 *   2. else the first tab that has one (the active tab can be the Start Page,
 *      which has no connection of its own);
 *   3. else the session's first declared connection — a connection-only
 *      session has no tabs at all, and resuming one must still bring something
 *      back.
 *
 * When `savedConnections` is supplied, candidates that CANNOT be auto-connected
 * (config deleted, or Entra) are skipped rather than spending an eager slot:
 * a resume whose active tab points at a deleted connection must still bring
 * something back instead of opening nothing. Every caller that has the list
 * (restore, validation, the featured card) passes it, so all three agree on
 * which connections the chips promise and the resume opens.
 *
 * Only branches 2 and 3 are capped at one connection — the active tab gets
 * everything it needs.
 */
export function resolveEagerConnectionIds(
  session: Pick<Session, 'tabs' | 'activeTabId' | 'connections'>,
  savedConnections?: Connection[]
): string[] {
  const viable = (id: string): boolean => {
    if (!savedConnections) return true;
    const connection = savedConnections.find((c) => c.id === id);
    return !!connection && !isEntraInteractiveConnection(connection);
  };

  const activeTab = session.tabs.find((tab) => tab.id === session.activeTabId);
  const activeTabIds = (activeTab ? tabConnectionIds(activeTab) : []).filter(viable);
  if (activeTabIds.length > 0) return activeTabIds;

  const fallback =
    session.tabs.flatMap((tab) => tabConnectionIds(tab)).find(viable) ??
    session.connections.map((conn) => conn.connectionId).find(viable);

  return fallback ? [fallback] : [];
}

/**
 * Replace the deferred set — one restore's deferrals never leak into the next.
 */
export function markConnectionsLazy(connectionIds: string[]): void {
  pendingLazyIds.clear();
  inFlightLazyConnects.clear();
  connectionIds.forEach((id) => pendingLazyIds.add(id));
  notifyLazyConnectPendingChanged();
}

export function isLazyConnectPending(connectionId: string): boolean {
  return pendingLazyIds.has(connectionId);
}

/**
 * Is this still-pending connection one the background sweep will NOT open?
 *
 * `connectPasswordPromptStore` holds a single pending request: opening a second
 * prompt resolves the first as cancelled, so a parallel sweep over several
 * password-gated connections would cancel its own prompts and report failures
 * the user never saw. Those connections keep the original contract — they open
 * the first time a tab needs them, with the click as the intent — and the
 * Object Explorer says so ("Needs password") rather than promising a connect
 * that isn't coming.
 */
export function isPasswordGatedLazyConnect(connectionId: string): boolean {
  if (!pendingLazyIds.has(connectionId)) return false;
  const connection = useConnectionStore
    .getState()
    .connections.find((c) => c.id === connectionId);
  return connectionNeedsPasswordPrompt(connection);
}

/**
 * Drop a connection out of lazy connect without connecting it.
 *
 * The guarantee "a connection the user disconnected by hand is never reopened
 * behind their back" needs this: a deferred connection that was never opened
 * can still be disconnected (Start Page / Connections dialog), and that path
 * does NOT close the tabs pointing at it — so without forgetting the id, the
 * next click on such a tab would connect it again.
 */
export function forgetLazyConnect(connectionId: string): void {
  if (pendingLazyIds.delete(connectionId)) notifyLazyConnectPendingChanged();
  inFlightLazyConnects.delete(connectionId);
  // Dropping our bookkeeping is not enough when the connect is already dialing:
  // it would land, be adopted, and `updateConnectionStatus` would re-add the
  // connection the user just removed. Withdraw the intent so the store closes
  // it on arrival and the continuation below declines to write.
  useConnectionStore.getState().abandonConnect(connectionId);
}

export function getPendingLazyConnectIds(): string[] {
  return Array.from(pendingLazyIds);
}

export function clearLazyConnectState(): void {
  // Same reasoning as `forgetLazyConnect`, for every id at once — this runs on
  // session teardown, where a lazy connect that lands afterwards would attach
  // the outgoing session's connection to the incoming one.
  const { abandonConnect } = useConnectionStore.getState();
  inFlightLazyConnects.forEach((_attempt, connectionId) => abandonConnect(connectionId));
  pendingLazyIds.clear();
  inFlightLazyConnects.clear();
  notifyLazyConnectPendingChanged();
}

/**
 * Connect a deferred connection because the user just needed it.
 *
 * Resolves `false` — never rejects — when there is nothing to do: the id was
 * not deferred, its saved config is gone, or the attempt failed. Callers are
 * activation handlers; a rejection there would surface as an unhandled
 * rejection, not as anything a user could act on.
 */
export function requestLazyConnect(connectionId: string): Promise<boolean> {
  const inFlight = inFlightLazyConnects.get(connectionId);
  if (inFlight) return inFlight;

  if (!pendingLazyIds.has(connectionId)) return Promise.resolve(false);

  const connectionStore = useConnectionStore.getState();
  const connection = connectionStore.connections.find((c) => c.id === connectionId);
  // Deliberately consumed even without a config: a session that references a
  // deleted connection must not re-ask on every activation. Consuming it here
  // is also what stops the Object Explorer calling a connection "waiting" while
  // it is already being dialed.
  pendingLazyIds.delete(connectionId);
  notifyLazyConnectPendingChanged();
  if (!connection) return Promise.resolve(false);

  // Which session this connect belongs to. `updateConnectionStatus` ADDS a
  // connection entry when the id isn't declared, so a connect that lands after
  // the user switched sessions would graft the outgoing session's connection
  // onto the incoming session's record.
  const sessionIdAtRequest = useSessionStore.getState().currentSession?.id ?? null;
  // Whether this request's intent still stands when the connect lands. The
  // session check alone is not enough: disconnecting a deferred connection
  // withdraws the intent WITHOUT changing session, and that is exactly the
  // sequence that used to re-add it.
  const intentEpochAtRequest = connectionStore.getConnectIntentEpoch(connectionId);

  const attempt = Promise.resolve()
    .then(() => connectionStore.connectToDatabase(connection, { initiator: 'restore' }))
    .then((success) => {
      // The session record still says 'disconnected' — that was true while the
      // connection was deferred, and several surfaces (the query toolbar's
      // execute button among them) read it as authoritative. Clearing it is
      // what makes the tab usable, exactly like the manual reconnect path does.
      // Deliberately NOT `removeConnectionFromSession`/a save: a lazy connect
      // is not a user disconnect and must not touch the pruning rules.
      const stillSameSession =
        (useSessionStore.getState().currentSession?.id ?? null) === sessionIdAtRequest;
      const intentStillStands =
        useConnectionStore.getState().getConnectIntentEpoch(connectionId) ===
        intentEpochAtRequest;
      if (success && stillSameSession && intentStillStands) {
        useSessionStore.getState().updateConnectionStatus(connectionId, 'connected');
      }
      return success;
    })
    .catch(() => false)
    .finally(() => {
      inFlightLazyConnects.delete(connectionId);
    });

  inFlightLazyConnects.set(connectionId, attempt);
  return attempt;
}

/**
 * How many background connects run at once.
 *
 * `withConnectLock` (electron/main.ts) serialises per CONNECTION ID, so
 * cross-connection parallelism is safe at the driver layer; the cap exists so a
 * ten-connection session doesn't open ten sockets, ten pools and ten language
 * services into the same moment the workspace is painting. Three keeps a
 * typical session's stragglers arriving within one dial window without making
 * the resume feel like a thundering herd.
 */
const LAZY_SWEEP_CONCURRENCY = 3;

/** What the background sweep did — counts only, for restore telemetry. */
export type LazyConnectSweepSummary = {
  /** Connections the sweep actually dialed. */
  attempted: number;
  /** …of which opened. */
  connected: number;
  /** …of which did not (unreachable, VPN-gated, bad credentials, …). */
  failed: number;
  /** Left for first use because opening them needs a password prompt. */
  awaitingPassword: number;
};

/**
 * Open everything the restore left pending, in the background.
 *
 * Called by `loadSession` AFTER the workspace is committed and never awaited:
 * the point of the split is that a session with a dead server still shows its
 * tabs instantly. Every guard lives in `requestLazyConnect` (at-most-once,
 * in-flight join, session-epoch and intent-epoch checks, `abandonConnect` on
 * withdrawal) — this is a scheduler over that one connect path, not a second
 * one.
 *
 * Re-checks `isLazyConnectPending` before each dial rather than trusting the
 * snapshot: a tab activation may have opened it already, or the user may have
 * disconnected it, and neither is a background failure worth reporting.
 */
export async function sweepPendingLazyConnects(
  concurrency: number = LAZY_SWEEP_CONCURRENCY
): Promise<LazyConnectSweepSummary> {
  const queue: string[] = [];
  let awaitingPassword = 0;
  for (const connectionId of getPendingLazyConnectIds()) {
    if (isPasswordGatedLazyConnect(connectionId)) {
      awaitingPassword += 1;
      continue;
    }
    queue.push(connectionId);
  }

  let attempted = 0;
  let connected = 0;
  let failed = 0;
  let cursor = 0;

  const worker = async (): Promise<void> => {
    while (cursor < queue.length) {
      const connectionId = queue[cursor];
      cursor += 1;
      // Consumed (tab activation) or withdrawn (user disconnect / session
      // switch) since the queue was built — not ours to attempt or to count.
      if (!isLazyConnectPending(connectionId)) continue;
      attempted += 1;
      const success = await requestLazyConnect(connectionId);
      if (success) connected += 1;
      else failed += 1;
    }
  };

  const workers = Math.min(Math.max(1, concurrency), queue.length);
  await Promise.all(Array.from({ length: workers }, () => worker()));

  return { attempted, connected, failed, awaitingPassword };
}
